import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import {
  behavioralToPickerId,
  classifyAndUpsertApp,
  classifyAppHeuristicOnly,
  evaluateLockbox,
  isClaudeClassificationAvailable,
  LOCKBOX_MODES,
  pickerIdToBehavioral,
  removeApp,
  runAllLockboxSandboxScenarios,
  runDeepClassification,
  setActiveMode,
  setEmergency,
  setNativeEnforcementMode,
  setUserDisturbingBlock,
  simulateEnforcement,
  upsertApp,
  useLockbox,
} from "../lockbox";
import { CategoryPicker } from "./CategoryPicker";
import {
  getEnforcementHealthNative,
  getInstalledAppsNative,
  lockboxEnforcementSupported,
  openUsageAccessSettingsNative,
  requestOverlayPermissionNative,
} from "../platform/lockboxBridge";
import { LockboxDebugDashboard } from "./LockboxDebugDashboard";

const DEMO_APPS = [
  { name: "Waze", category: "navigation", packageName: "com.waze", bundleId: "com.waze.iphone" },
  {
    name: "Crunchyroll",
    category: "anime_streaming",
    packageName: "com.crunchyroll.crunchyroid",
  },
  {
    name: "TikTok",
    category: "shortform_feed",
    packageName: "com.zhiliaoapp.musically",
    bundleId: "com.zhiliaoapp.musically",
  },
];

const ENFORCEMENT_MODES = [
  { id: "monitor", label: "Monitor" },
  { id: "soft", label: "Soft" },
  { id: "strict", label: "Strict" },
];

/**
 * Lockbox Lab — app inventory, genre rules, Android enforcement, decision debugger.
 * @param {{ standalone?: boolean }} [props] standalone=true: full page (always open). false: collapsible section inside Parental Filter.
 */
export function LockboxLabPanel({ standalone = false } = {}) {
  const state = useLockbox();
  const [expanded, setExpanded] = useState(standalone);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugSim, setDebugSim] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [installed, setInstalled] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [classifyingId, setClassifyingId] = useState(/** @type {string|null} */ (null));
  const [addEditorOpen, setAddEditorOpen] = useState(false);
  const [pendingAdd, setPendingAdd] = useState(/** @type {{ label: string, packageName: string }|null} */ (null));
  const [addPickerId, setAddPickerId] = useState("other");
  const [addDetectedCategory, setAddDetectedCategory] = useState("unknown");
  const [addDisturbing, setAddDisturbing] = useState(false);

  const androidReady = Platform.OS === "android" && lockboxEnforcementSupported();

  const refreshHealth = useCallback(async () => {
    if (!androidReady) return;
    setHealth(await getEnforcementHealthNative());
  }, [androidReady]);

  useEffect(() => {
    void refreshHealth();
  }, [refreshHealth, state.lastPlan]);

  const openInstalledPicker = async () => {
    if (!androidReady) {
      Alert.alert("Android only", "Installed-app picker requires a native Android build.");
      return;
    }
    setPickerLoading(true);
    setPickerOpen(true);
    try {
      const apps = await getInstalledAppsNative();
      apps.sort((a, b) => a.label.localeCompare(b.label));
      setInstalled(apps);
    } catch (e) {
      Alert.alert("Apps", e?.message ? String(e.message) : "Could not load installed apps.");
      setPickerOpen(false);
    } finally {
      setPickerLoading(false);
    }
  };

  const openAddEditor = (row) => {
    const quick = classifyAppHeuristicOnly(row.packageName, row.label);
    const detected = quick.classification.primaryCategory;
    setPendingAdd(row);
    setAddDetectedCategory(detected);
    setAddPickerId(behavioralToPickerId(detected));
    setAddDisturbing(false);
    setAddEditorOpen(true);
    setPickerOpen(false);
  };

  const confirmAddApp = async () => {
    if (!pendingAdd) return;
    setClassifyingId(pendingAdd.packageName);
    try {
      const behavioral = pickerIdToBehavioral(addPickerId);
      const quick = classifyAppHeuristicOnly(pendingAdd.packageName, pendingAdd.label);
      const classification = {
        ...quick.classification,
        primaryCategory: behavioral,
        classificationMeta: {
          ...quick.classification.classificationMeta,
          source: "heuristic",
          classifiedAt: new Date().toISOString(),
        },
      };
      const row = await classifyAndUpsertApp({
        displayName: pendingAdd.label,
        packageName: pendingAdd.packageName,
        nativeRef: null,
        classification,
        runDeepClassification: true,
      });
      if (addDisturbing) setUserDisturbingBlock(row.id);
    } finally {
      setClassifyingId(null);
      setAddEditorOpen(false);
      setPendingAdd(null);
    }
  };

  const openDecisionDebugger = () => {
    const world = {
      now: new Date(),
      activeModeId: state.activeModeId,
      emergency: {
        active: state.emergency.active,
        until: state.emergency.until ? new Date(state.emergency.until) : null,
      },
      apps: state.inventory,
    };
    const plan = evaluateLockbox(state.rulepack, world);
    const sim = simulateEnforcement(
      { ...plan, enforcementMode: state.nativeEnforcementMode },
      {
        enforcementMode: androidReady ? "production" : "sandbox",
        labelResolver: (id) => state.inventory.find((a) => a.id === id)?.displayName ?? id,
        includeDecisionTraces: true,
        evaluationContext: { rulepack: state.rulepack, world },
      },
    );
    setDebugSim(sim);
    setDebugOpen(true);
  };

  const summary = useMemo(() => {
    const p = state.lastPlan;
    if (!p) return "—";
    return `${p.blockAppIds.length} restrict · ${p.allowAppIds.length} allow`;
  }, [state.lastPlan]);

  const inventoryIds = new Set(state.inventory.map((a) => a.packageName || a.bundleId));

  const labContent = (
    <>
            {androidReady ? (
              <>
                <Text style={styles.label}>Enforcement (Android)</Text>
                <View style={styles.rowWrap}>
                  {ENFORCEMENT_MODES.map((m) => (
                    <Pressable
                      key={m.id}
                      onPress={() => setNativeEnforcementMode(m.id)}
                      style={[styles.chip, state.nativeEnforcementMode === m.id ? styles.chipOn : null]}
                    >
                      <Text
                        style={[styles.chipText, state.nativeEnforcementMode === m.id ? styles.chipTextOn : null]}
                      >
                        {m.label}
                      </Text>
                    </Pressable>
                  ))}
                </View>
                <Text style={styles.health}>
                  Usage access: {health?.usageStatsGranted ? "✓" : "✗"} · Overlay:{" "}
                  {health?.overlayGranted ? "✓" : "✗"} · Service: {health?.serviceRunning ? "on" : "off"}
                </Text>
                <View style={styles.rowWrap}>
                  <Pressable style={styles.permBtn} onPress={openUsageAccessSettingsNative}>
                    <Text style={styles.permBtnText}>Usage access</Text>
                  </Pressable>
                  <Pressable style={styles.permBtn} onPress={requestOverlayPermissionNative}>
                    <Text style={styles.permBtnText}>Overlay</Text>
                  </Pressable>
                  <Pressable style={styles.permBtn} onPress={() => void refreshHealth()}>
                    <Text style={styles.permBtnText}>Refresh</Text>
                  </Pressable>
                </View>
              </>
            ) : null}

            <Text style={styles.label}>Focus mode</Text>
            <View style={styles.rowWrap}>
              {LOCKBOX_MODES.map((m) => (
                <Pressable
                  key={m.id}
                  onPress={() => setActiveMode(m.id)}
                  style={[styles.chip, state.activeModeId === m.id ? styles.chipOn : null]}
                >
                  <Text style={[styles.chipText, state.activeModeId === m.id ? styles.chipTextOn : null]}>
                    {m.label}
                  </Text>
                </Pressable>
              ))}
            </View>

            <Pressable onPress={() => setEmergency(!state.emergency.active, null)} style={styles.emergencyBtn}>
              <Text style={styles.emergencyText}>
                {state.emergency.active ? "Turn off emergency pause" : "Emergency: pause automatic blocks"}
              </Text>
            </Pressable>

            <Text style={styles.label}>Add apps</Text>
            <View style={styles.rowWrap}>
              {androidReady ? (
                <Pressable style={styles.chip} onPress={openInstalledPicker}>
                  <Text style={styles.chipText}>+ From device</Text>
                </Pressable>
              ) : null}
              {DEMO_APPS.map((d) => (
                <Pressable
                  key={d.name}
                  onPress={() =>
                    void classifyAndUpsertApp({
                      displayName: d.name,
                      nativeRef: null,
                      bundleId: d.bundleId,
                      packageName: d.packageName,
                    })
                  }
                  style={styles.chip}
                >
                  <Text style={styles.chipText}>+ {d.name}</Text>
                </Pressable>
              ))}
            </View>

            <Text style={styles.label}>Apps ({state.inventory.length})</Text>
            {state.inventory.length === 0 ? <Text style={styles.muted}>Add apps from device or demos.</Text> : null}
            {state.inventory.map((a) => (
              <View key={a.id} style={styles.appRow}>
                <Text style={styles.appName}>
                  {a.displayName} · {a.classification.primaryCategory}
                  {a.packageName ? `\n${a.packageName}` : ""}
                </Text>
                <Pressable onPress={() => removeApp(a.id)}>
                  <Text style={styles.remove}>Remove</Text>
                </Pressable>
              </View>
            ))}

            <Pressable style={styles.debugBtn} onPress={openDecisionDebugger}>
              <Text style={styles.debugBtnText}>Why restricted? (decision debugger)</Text>
            </Pressable>

            <Pressable
              style={styles.scenarioBtn}
              onPress={() => {
                const rows = runAllLockboxSandboxScenarios();
                const failed = rows.filter((r) => !r.ok);
                Alert.alert(
                  "Sandbox scenarios",
                  failed.length === 0
                    ? `All ${rows.length} passed.`
                    : `${failed.length} failed.\n\n${rows.map((r) => `${r.ok ? "✓" : "✗"} ${r.message}`).join("\n")}`,
                );
              }}
            >
              <Text style={styles.scenarioBtnText}>Run sandbox scenarios</Text>
            </Pressable>
    </>
  );

  return (
    <>
      <View style={styles.box}>
        {standalone ? (
          <>
            <Text style={styles.standaloneTitle}>Lockbox Lab</Text>
            <Text style={styles.sub}>
              {summary} · mode: {state.activeModeId}
              {androidReady ? ` · enforce: ${state.nativeEnforcementMode}` : " · intent only"}
            </Text>
            <Text style={styles.invariant}>
              Block apps by behavioral genre (e.g. block manga apps, allow WhatsApp) — not by Play Store age rating.
            </Text>
            {labContent}
          </>
        ) : (
          <>
            <Pressable onPress={() => setExpanded((e) => !e)} style={styles.header}>
              <Text style={styles.title}>Lockbox Lab</Text>
              <Text style={styles.chev}>{expanded ? "▼" : "▶"}</Text>
            </Pressable>
            <Text style={styles.sub}>
              {summary} · mode: {state.activeModeId}
              {androidReady ? ` · enforce: ${state.nativeEnforcementMode}` : " · intent only"}
            </Text>
            {!expanded ? (
              <Text style={styles.tapHint}>Tap ▶ to open — app blocking by genre, not age rating</Text>
            ) : (
              <Text style={styles.invariant}>
                Block apps by genre (manga, short-form, etc.). Separate from DNS filter above.
              </Text>
            )}
            {expanded ? labContent : null}
          </>
        )}
      </View>

      <Modal visible={pickerOpen} animationType="slide" onRequestClose={() => setPickerOpen(false)}>
        <View style={styles.pickerSheet}>
          <View style={styles.pickerHeader}>
            <Text style={styles.pickerTitle}>Installed apps</Text>
            <Pressable onPress={() => setPickerOpen(false)}>
              <Text style={styles.pickerClose}>Close</Text>
            </Pressable>
          </View>
          {pickerLoading ? <Text style={styles.muted}>Loading…</Text> : null}
          <ScrollView>
            {installed.map((row) => {
              const already = inventoryIds.has(row.packageName);
              return (
                <Pressable
                  key={row.packageName}
                  style={styles.pickerRow}
                  disabled={already}
                  onPress={() => openAddEditor(row)}
                >
                  <Text style={styles.pickerLabel}>{row.label}</Text>
                  <Text style={styles.pickerPkg}>{row.packageName}</Text>
                  {already ? <Text style={styles.pickerAdded}>Added</Text> : null}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      </Modal>

      <Modal visible={addEditorOpen} animationType="slide" onRequestClose={() => setAddEditorOpen(false)}>
        <View style={styles.pickerSheet}>
          <Text style={styles.pickerTitle}>Add app</Text>
          {pendingAdd ? <Text style={styles.pickerPkg}>{pendingAdd.label}</Text> : null}
          {pendingAdd ? (
            <CategoryPicker
              detectedCategory={addDetectedCategory}
              pickerCategoryId={addPickerId}
              onPickerCategoryChange={setAddPickerId}
              disturbing={addDisturbing}
              onDisturbingChange={setAddDisturbing}
            />
          ) : null}
          <Pressable style={styles.debugBtn} onPress={() => void confirmAddApp()} disabled={!!classifyingId}>
            <Text style={styles.debugBtnText}>{classifyingId ? "Classifying…" : "Save & classify"}</Text>
          </Pressable>
          <Pressable style={styles.closeAdd} onPress={() => setAddEditorOpen(false)}>
            <Text style={styles.pickerClose}>Cancel</Text>
          </Pressable>
        </View>
      </Modal>

      <LockboxDebugDashboard
        visible={debugOpen}
        onClose={() => setDebugOpen(false)}
        simulation={debugSim}
        planMode={state.activeModeId}
      />
    </>
  );
}

const styles = StyleSheet.create({
  box: {
    marginTop: 14,
    padding: 12,
    borderRadius: 10,
    backgroundColor: "#eef2ff",
    borderWidth: 1,
    borderColor: "#c7d2fe",
  },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  title: { fontSize: 15, fontWeight: "700", color: "#312e81" },
  standaloneTitle: { fontSize: 18, fontWeight: "800", color: "#312e81", marginBottom: 4 },
  tapHint: { marginTop: 6, fontSize: 12, color: "#4338ca", fontWeight: "600" },
  chev: { color: "#4338ca", fontSize: 14 },
  sub: { marginTop: 6, color: "#4c1d95", fontSize: 12 },
  invariant: { marginTop: 8, color: "#5b21b6", fontSize: 11, lineHeight: 16 },
  label: { marginTop: 10, fontWeight: "700", color: "#3730a3", fontSize: 12 },
  health: { marginTop: 6, fontSize: 11, color: "#4338ca" },
  rowWrap: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 6 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "#e0e7ff",
  },
  chipOn: { backgroundColor: "#4f46e5" },
  chipText: { color: "#312e81", fontSize: 12, fontWeight: "600" },
  chipTextOn: { color: "#fff" },
  permBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    backgroundColor: "#c7d2fe",
  },
  permBtnText: { color: "#312e81", fontSize: 11, fontWeight: "700" },
  emergencyBtn: { marginTop: 10, padding: 10, borderRadius: 8, backgroundColor: "#fef3c7" },
  emergencyText: { color: "#92400e", fontWeight: "700", fontSize: 12, textAlign: "center" },
  muted: { color: "#6b7280", fontSize: 12, fontStyle: "italic" },
  appRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", paddingVertical: 8 },
  appName: { color: "#1e1b4b", fontSize: 13, fontWeight: "600" },
  appPkg: { color: "#6b7280", fontSize: 11, marginTop: 2 },
  severityBadge: { fontSize: 11, marginTop: 4, fontWeight: "600" },
  appActions: { alignItems: "flex-end", gap: 6 },
  reclassify: { color: "#4338ca", fontWeight: "700", fontSize: 12 },
  remove: { color: "#b91c1c", fontWeight: "700", fontSize: 12 },
  scenarioBtn: {
    marginTop: 12,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#4338ca",
    alignItems: "center",
  },
  scenarioBtnText: { color: "#fff", fontWeight: "700", fontSize: 13 },
  debugBtn: {
    marginTop: 10,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: "#0f766e",
    alignItems: "center",
  },
  debugBtnText: { color: "#fff", fontWeight: "700", fontSize: 13 },
  pickerSheet: { flex: 1, backgroundColor: "#fff", paddingTop: 48, paddingHorizontal: 16 },
  pickerHeader: { flexDirection: "row", justifyContent: "space-between", marginBottom: 12 },
  pickerTitle: { fontSize: 18, fontWeight: "700" },
  pickerClose: { color: "#2563eb", fontWeight: "600" },
  pickerRow: { paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: "#e5e7eb" },
  pickerLabel: { fontSize: 15, fontWeight: "600", color: "#111" },
  pickerPkg: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  pickerAdded: { fontSize: 11, color: "#059669", marginTop: 4 },
  closeAdd: { marginTop: 16, alignItems: "center" },
});
